import { RewardsComponents } from '../components'
import { useStatusBarColor } from '@/src/common/hooks/useStatusBarColor'
import { TouchableOpacity, View, Text, StyleSheet } from 'react-native'
import { useMediaQuery } from 'react-responsive'
import { RewardsGQL } from '../gql'
import { RewardsFilter, TRANSITION_TO_SPLIT_SCREEN_WIDTH } from '../types'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useCallback, useEffect, useState } from 'react'
import { usePointsLedgerView } from '../hooks/usePointsLedgerView'
import { ChevronLeft } from '@/src/assets/icons/ChevronLeft'
import { routerBack } from '@/src/common/utils/routerBack'
import { topTabBarHeight, useIsTabBarPositionedTop } from '@/src/common/components/TopTabBar'
import { maxWidth, useMainWrapperPadding } from '@/src/common/wrappers/MainWrapper'
import { usePoints } from '@/src/common/hooks/usePoints'
import * as Surveys from '@/src/experiences/surveys'
import { RewardsE2E } from '../e2e'
import { mesaColorNames, typography } from '@/src/common/theme'

const styles = StyleSheet.create({
  screen: {
    backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
    position: 'relative',
    flex: 1,
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    display: 'flex',
  },
  pageHeader: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 32,
    paddingBottom: 5,
  },
  pageHeaderTitle: {
    ...typography['heading/heading3'],
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
  },
  backButton: {
    flex: 0,
    flexBasis: 'auto',
    position: 'absolute',
    left: 32,
  },
  backgroundTop: {
    backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
    flexGrow: 1,
  },
  backgroundBottom: {
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    flexGrow: 1,
  },
  content: {
    flex: 1,
    flexGrow: 1,
    position: 'relative',
    zIndex: 1,
    gap: 32,
    maxWidth,
    marginHorizontal: 'auto',
    width: '100%',
  },
  mainContent: {
    flex: 1,
    flexGrow: 1,
    gap: 32,
  },
  activityFeedSection: {
    flex: 1,
  },
})

function PointsLedger() {
  useStatusBarColor()
  const isSplitScreen = useMediaQuery({ minWidth: TRANSITION_TO_SPLIT_SCREEN_WIDTH })
  const isTabBarPositionedTop = useIsTabBarPositionedTop()
  const [view, setView] = useState<RewardsFilter>('All')
  const { data: balanceData, refetch: refetchBalance } = usePoints().query
  const { isLoading, refresh, ledger, fetchNextPage, hasMore } = RewardsGQL.usePointsLedger()
  const { sections } = usePointsLedgerView(view)
  Surveys.Hooks.useScreenUnmountTrigger('rewards_points_ledger_viewed')

  useEffect(() => {
    void refresh()
  }, [refresh])

  const handleRefresh = useCallback(async () => {
    await refetchBalance()
    await refresh()
  }, [refetchBalance, refresh])

  const handleEndReached = useCallback(async () => {
    if (hasMore) {
      await fetchNextPage()
    }
  }, [fetchNextPage, hasMore])

  const mainWrapperPadding = useMainWrapperPadding()

  return (
    <SafeAreaView
      edges={['top']}
      style={[styles.screen, { paddingTop: isTabBarPositionedTop ? topTabBarHeight + 32 : 16 }]}
    >
      <View style={styles.background}>
        <View style={styles.backgroundTop} />
        <View style={styles.backgroundBottom} />
      </View>
      <View
        style={[styles.content, { paddingHorizontal: mainWrapperPadding }]}
        testID={RewardsE2E.Ids.View.PointsLedger}
      >
        <View style={[styles.pageHeader, { marginHorizontal: -mainWrapperPadding }]}>
          <TouchableOpacity
            onPress={() => void routerBack()}
            style={[styles.backButton, { display: 'flex' }]}
            testID={RewardsE2E.Ids.Press.PointsLedgerBack}
          >
            <ChevronLeft size={24} color={mesaColorNames['Mesa - Brand/White #FFFFFF']} />
          </TouchableOpacity>
          <Text style={[styles.pageHeaderTitle, { marginLeft: mainWrapperPadding }]}>
            Mesa Points
          </Text>
        </View>
        <View style={[styles.mainContent, { flexDirection: isSplitScreen ? 'row' : 'column' }]}>
          <View style={isSplitScreen ? { flex: 1 } : { paddingHorizontal: 16 }}>
            <RewardsComponents.BalanceHeader
              currentPoints={balanceData?.getPointsBalance.active.value}
              lifetimeEarnedPoints={balanceData?.getPointsBalance.lifetimeEarned.value}
              lifetimeRedeemedPoints={balanceData?.getPointsBalance.lifetimeSpent.value}
              view="full"
            />
          </View>
          <View style={styles.activityFeedSection}>
            <RewardsComponents.ActivityFeed
              view={view}
              onSelectView={setView}
              isRefreshing={isLoading && ledger.length === 0}
              onRefresh={() => void handleRefresh()}
              onEndReached={() => void handleEndReached()}
              isEndReachedLoading={isLoading && ledger.length > 0}
              sections={sections}
            />
          </View>
        </View>
      </View>
    </SafeAreaView>
  )
}

function PointsLedgerContextProvider() {
  return (
    <RewardsGQL.PointsLedgerProvider>
      <PointsLedger />
    </RewardsGQL.PointsLedgerProvider>
  )
}

export const PointsLedgerScreen = {
  name: 'points-ledger',
  Screen: PointsLedgerContextProvider,
} as const
